"use client";

import React, { useState, useEffect, MouseEvent } from "react";
import { useRouter } from "next/navigation";

interface PopupData {
  imageUrl: string;
  title: string;
  description: string;
  promocode: string;
  active: boolean;
}

interface PopupClientProps {
  popupData: PopupData;
}

export default function PopupClient({ popupData }: PopupClientProps) {
  // Initially, do not show the popup until delay passes.
  const [isOpen, setIsOpen] = useState(false);
  const [hasDelayed, setHasDelayed] = useState(false);
  const router = useRouter();

  // Delay showing the popup for 4 seconds.
  useEffect(() => {
    const timer = setTimeout(() => {
      setHasDelayed(true);
      setIsOpen(true);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Until delay passes, render nothing.
  if (!hasDelayed) {
    return null;
  }

  const { imageUrl, title, description, promocode } = popupData;

  // Close the popup if the overlay is clicked.
  const handleOverlayClick = () => {
    setIsOpen(false);
  };

  // Prevent closing when clicking inside the popup container.
  const handlePopupContainerClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  // On button click, close popup then redirect to /quote?promocode=PROMOCODE.
  const handleSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsOpen(false);
    router.push(`/quote?promocode=${encodeURIComponent(promocode)}`);
  };

  return (
    <>
      {/* Discount Bubble (shown when popup is closed) */}
      <div
        className={`${
          isOpen ? "hidden" : "flex"
        } fixed left-5 top-1/2 transform -translate-y-1/2 w-[50px] h-[50px] md:w-[60px] md:h-[60px] bg-[#23c45e] rounded-full text-white text-2xl font-bold items-center justify-center cursor-pointer z-[9999]`}
        onClick={() => setIsOpen(true)}
      >
        %
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
          onClick={handleOverlayClick}
        >
          <div
            onClick={handlePopupContainerClick}
            className="relative flex flex-col md:flex-row bg-white rounded-lg overflow-hidden w-full md:w-[800px] max-w-[90%] md:h-[500px] transition-all duration-300 popup-appear"
          >
            {/* LEFT: Image area */}
            <div
              className="w-full md:w-1/2 h-48 md:h-auto bg-cover bg-center"
              style={{ backgroundImage: `url(${imageUrl})` }}
            >
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-2 left-2 bg-transparent w-8 h-8"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 50 50"
                  className="fill-white"
                >
                  <path d="M25,2C12.319,2,2,12.319,2,25s10.319,23,23,23s23-10.319,23-23S37.681,2,25,2z M33.71,32.29c0.39,0.39,0.39,1.03,0,1.42  C33.51,33.9,33.26,34,33,34s-0.51-0.1-0.71-0.29L25,26.42l-7.29,7.29C17.51,33.9,17.26,34,17,34s-0.51-0.1-0.71-0.29  c-0.39-0.39-0.39-1.03,0-1.42L23.58,25l-7.29-7.29c-0.39-0.39-0.39-1.03,0-1.42c0.39-0.39,1.03-0.39,1.42,0L25,23.58l7.29-7.29  c0.39-0.39,1.03-0.39,1.42,0c0.39,0.39,0.39,1.03,0,1.42L26.42,25L33.71,32.29z" />
                </svg>
              </button>
            </div>

            {/* RIGHT: Content area */}
            <div className="w-full md:w-1/2 flex flex-col p-5 justify-center">
              <h2 className="text-2xl font-bold mb-2 text-center uppercase">
                {title}
              </h2>
              <p className="text-sm mb-4 text-center">{description}</p>
              <div className="bg-gray-200 text-black py-3 px-4 rounded-md font-medium inline-block mb-4 text-center uppercase">
                {promocode}
              </div>
              {/* Full-Width Button */}
              <button
                onClick={handleSubmit}
                className="w-full uppercase flex items-center justify-center gap-2 px-6 py-3 text-[14px] sm:text-[16px] text-white bg-green-500 rounded hover:bg-green-600 transition-all duration-500 ease-in-out"
              >
                Get My Discount
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 12 12"
                  width="12"
                  height="12"
                  className="fill-current"
                >
                  <path d="M10.0035 3.40804L1.41153 12L0 10.5885L8.59097 1.99651H1.01922V0H12V10.9808H10.0035V3.40804Z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes popupAppear {
          0% {
            opacity: 0;
            transform: scale(0.95);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        .popup-appear {
          animation: popupAppear 0.3s ease-in-out forwards;
        }
      `}</style>
    </>
  );
}
